import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/app-store'

export function useAuth() {
  const { user, loading, setUser, setLoading, logout } = useAuthStore()
  const queryClient = useQueryClient()

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email!,
            name: session.user.user_metadata?.name || session.user.email!,
            role: session.user.user_metadata?.role || 'user',
            avatar: session.user.user_metadata?.avatar_url,
            franchise_ids: session.user.user_metadata?.franchise_ids || []
          })
        } else if (event === 'SIGNED_OUT') {
          logout()
          if (typeof (queryClient as any).clear === 'function') {
            (queryClient as any).clear()
          } else {
            void queryClient.invalidateQueries()
            queryClient.getMutationCache?.().clear?.()
          }
        }
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [setUser, setLoading, logout, queryClient])

  // Get initial session
  useEffect(() => {
    void supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email!,
            name: session.user.user_metadata?.name || session.user.email!,
            role: session.user.user_metadata?.role || 'user',
            avatar: session.user.user_metadata?.avatar_url,
            franchise_ids: session.user.user_metadata?.franchise_ids || []
          })
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setUser, setLoading])

  const signInMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      return data
    }
  })

  const signOutMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    onSuccess: () => {
      logout()
      queryClient.clear()
    }
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      // Redirect to a page where they can set a new password
      // For now, we'll just use the login page or a dedicated reset page if we had one
      const redirectTo = `${window.location.origin}/reset-password` 
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      })
      if (error) throw error
    }
  })

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signIn: signInMutation.mutateAsync,
    signOut: signOutMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    isSigningIn: Boolean(signInMutation.isPending),
    isSigningOut: Boolean(signOutMutation.isPending),
    isResettingPassword: Boolean(resetPasswordMutation.isPending)
  }
}
